/**
 * CaptionScene Component
 *
 * Phase 2 scene for E-Commerce video templates.
 * Displays LLM-generated caption with configurable text animation over gradient background.
 *
 * Features:
 * - Full-screen gradient background with template-specific colors
 * - Multiple text animation options (typewriter, word_reveal, blur_fade, etc.)
 * - Swipe-up indicator
 * - Smooth fade-in transition
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import { AnimatedText, TextAnimationType } from './AnimatedText';
import { GradientBackground } from './GradientBackground';
import { CTAButton } from './CTAButton';
import { CaptionFontId, FONT_ID_TO_FAMILY } from '../types/templates';

/** CTA translations keyed by language code */
const CTA_TRANSLATIONS: Record<string, { buttonText: string; subtext: string }> = {
  de: { buttonText: 'Link in Bio', subtext: 'NACH OBEN WISCHEN' },
  en: { buttonText: 'Link in Bio', subtext: 'SWIPE UP TO SHOP' },
  it: { buttonText: 'Link in Bio', subtext: 'SCORRI VERSO L\'ALTO' },
  el: { buttonText: 'Link in Bio', subtext: 'ΣΎΡΕ ΠΡΟΣ ΤΑ ΠΆΝΩ' },
  pt: { buttonText: 'Link na Bio', subtext: 'DESLIZE PARA CIMA' },
  fr: { buttonText: 'Lien en Bio', subtext: 'GLISSER VERS LE HAUT' },
  es: { buttonText: 'Link en Bio', subtext: 'DESLIZA HACIA ARRIBA' },
  nl: { buttonText: 'Link in Bio', subtext: 'VEEG OMHOOG' },
  pl: { buttonText: 'Link w Bio', subtext: 'PRZESUŃ W GÓRĘ' },
  tr: { buttonText: 'Biyografi Linki', subtext: 'YUKARI KAYDIR' },
  ru: { buttonText: 'Ссылка в Bio', subtext: 'СВАЙП ВВЕРХ' },
  ja: { buttonText: 'プロフィールのリンク', subtext: '上にスワイプ' },
  ko: { buttonText: '프로필 링크', subtext: '위로 스와이프' },
  zh: { buttonText: '主页链接', subtext: '向上滑动' },
  ar: { buttonText: 'الرابط في البايو', subtext: 'اسحب لأعلى' },
  hi: { buttonText: 'बायो में लिंक', subtext: 'ऊपर स्वाइप करें' },
  sv: { buttonText: 'Länk i Bio', subtext: 'SVEP UPPÅT' },
  da: { buttonText: 'Link i Bio', subtext: 'SWIPE OP' },
  no: { buttonText: 'Lenke i Bio', subtext: 'SVEIP OPP' },
  fi: { buttonText: 'Linkki Biossa', subtext: 'PYYHKÄISE YLÖS' },
  cs: { buttonText: 'Odkaz v Bio', subtext: 'PŘEJEĎTE NAHORU' },
  ro: { buttonText: 'Link în Bio', subtext: 'GLISEAZĂ ÎN SUS' },
  hu: { buttonText: 'Link a Bióban', subtext: 'HÚZD FELFELÉ' },
  uk: { buttonText: 'Посилання в Bio', subtext: 'СВАЙП ВГОРУ' },
  bg: { buttonText: 'Линк в Bio', subtext: 'ПЛЪЗНЕТЕ НАГОРЕ' },
  hr: { buttonText: 'Link u Bio', subtext: 'POVUCITE GORE' },
  th: { buttonText: 'ลิงก์ใน Bio', subtext: 'ปัดขึ้น' },
};

function getCtaTexts(language?: string): { buttonText: string; subtext: string } {
  if (language && CTA_TRANSLATIONS[language]) {
    return CTA_TRANSLATIONS[language];
  }
  return CTA_TRANSLATIONS.en;
}

export interface CaptionSceneProps {
  /** Caption text to display */
  captionText: string;
  /** Caption text color (hex) */
  captionColor?: string;
  /** Caption font ID (maps to font family) */
  captionFont?: CaptionFontId;
  /** Frame when this scene starts (relative to video start) */
  startFrame: number;
  /** Gradient preset name, 'custom', or 'none' for transparent */
  gradientPreset?: 'midnight' | 'fire' | 'ocean' | 'sunset' | 'custom' | 'none';
  /** Custom gradient colors (when preset is 'custom') */
  customGradientColors?: string[];
  /** Show swipe-up indicator */
  showSwipeUp?: boolean;
  /** Text animation type (default: typewriter for backwards compatibility) */
  textAnimation?: TextAnimationType;
  /** Language code for CTA texts (default: 'de') */
  language?: string;
}

export const CaptionScene: React.FC<CaptionSceneProps> = ({
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  startFrame,
  gradientPreset = 'midnight',
  customGradientColors,
  showSwipeUp = true,
  textAnimation = 'typewriter',
  language = 'de',
}) => {
  const ctaTexts = getCtaTexts(language);
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Calculate frame relative to scene start
  const relativeFrame = frame - startFrame;

  // Don't render if scene hasn't started
  if (relativeFrame < 0) {
    return null;
  }

  // Scene fade-in (0.5s)
  const sceneOpacity = interpolate(
    relativeFrame,
    [0, fps * 0.5],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Text animation starts after scene fades in (0.3s delay)
  // Use absolute frame by adding scene startFrame + delay
  const textStartFrame = startFrame + fps * 0.3;

  // CTA Button timing - appears after caption is animated (~4.5s into Phase 2)
  // Use absolute frame by adding scene startFrame + delay
  const ctaStartFrame = startFrame + fps * 4.5;

  // Resolve font family from font ID
  const fontFamily = FONT_ID_TO_FAMILY[captionFont] || 'Inter, sans-serif';

  // Adjust animation speed based on type
  // Word-based animations need different timing than typewriter
  const getAnimationProps = () => {
    const baseProps = {
      text: captionText,
      startFrame: textStartFrame,
      color: captionColor,
      fontSize: 62,
      fontWeight: 700,
      fontFamily,
      textAlign: 'center' as const,
      lineHeight: 1.35,
      maxWidth: 950,
      animationType: textAnimation,
    };

    // Typewriter uses charsPerSecond
    if (textAnimation === 'typewriter') {
      return {
        ...baseProps,
        charsPerSecond: 20,
        showCursor: false,
      };
    }

    // Word-based animations use wordDelay
    // Faster delay for energetic animations, slower for elegant ones
    const wordDelays: Record<TextAnimationType, number> = {
      typewriter: 4, // Not used but needed for type
      word_reveal: 5,
      blur_fade: 4,
      slide_up: 3,
      fade_scale: 4,
      bounce: 3,
      wave: 3,
      flip: 4,
    };

    return {
      ...baseProps,
      wordDelay: wordDelays[textAnimation] || 4,
    };
  };

  // Content shared between gradient and transparent modes
  const captionContent = (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 60,
      }}
    >
      {/* Caption Text with configurable animation */}
      <div style={{ maxWidth: 950, textAlign: 'center' }}>
        <AnimatedText {...getAnimationProps()} />
      </div>

      {/* CTA Button - Link in Bio */}
      {showSwipeUp && (
        <div
          style={{
            position: 'absolute',
            bottom: 120,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
          }}
        >
          <CTAButton
            text={ctaTexts.buttonText}
            startFrame={ctaStartFrame}
            backgroundColor="#FF6B35"
            textColor="#ffffff"
            fontSize={42}
            paddingX={56}
            paddingY={22}
            borderRadius={60}
            enablePulse={true}
            showArrow={true}
            arrowDirection="up"
            entranceAnimation="slideUp"
          />
          <div
            style={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: 24,
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              letterSpacing: 2,
              opacity: interpolate(
                frame,
                [ctaStartFrame + fps * 0.5, ctaStartFrame + fps * 0.8],
                [0, 1],
                { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
              ),
            }}
          >
            {ctaTexts.subtext}
          </div>
        </div>
      )}
    </AbsoluteFill>
  );

  // Transparent mode - no background, just the content
  if (gradientPreset === 'none') {
    return (
      <AbsoluteFill style={{ opacity: sceneOpacity }}>
        {captionContent}
      </AbsoluteFill>
    );
  }

  // Gradient mode - wrap content with gradient background
  return (
    <AbsoluteFill style={{ opacity: sceneOpacity }}>
      <GradientBackground
        preset={gradientPreset === 'custom' ? 'custom' : gradientPreset}
        customColors={customGradientColors}
        animate={true}
        animationSpeed={0.2}
        enableNoise={true}
        noiseOpacity={0.02}
      >
        {captionContent}
      </GradientBackground>
    </AbsoluteFill>
  );
};
